
import { BlogPost } from '@/types/blog';

export const post: BlogPost = {
  slug: "deep-dive-design-and-engineering",
  title: "Deep Dive: Using Fordje During Design and Engineering",
  excerpt: "Design and engineering is where every detail matters. Learn how Fordje helps teams build to spec from the start, preventing costly rework and permit delays.",
  content: `
    <p>
      You've picked the site. You've validated it can work. Now it's time to draw up exactly what you'll build, and make sure it gets permitted.
    </p>
    
    <p>
      Design and engineering is where every detail matters. A wrong setback assumption, a missed height restriction, or a forgotten design overlay can lead to rework, delayed permits, or worse, failed inspections.
    </p>
    
    <p>
      In this phase, the question isn't <em>where</em> you can build, it's <em>what</em> you're actually allowed to build, and <em>how</em> you communicate that in your final design.
    </p>
    
    <h2>What Design and Engineering Looks Like Today</h2>
    
    <p>
      In clean tech, engineering teams are building out technical diagrams for both permit and construction sets. These need to align perfectly with AHJ requirements, especially when you're trying to get the permit right the first time.
    </p>
    
    <p>
      In housing and construction, this means drawing full plot maps, building elevations, and everything that goes into the construction set. It's common to meet with the city or planning department during this phase to confirm specific details.
    </p>
    
    <p>
      Whether you're working in clean tech or housing, the goal is the same: design your project to match local requirements from the start, so you don't have to redo anything later.
    </p>
    
    <h2>How Fordje Helps</h2>
    
    <p>
      Fordje gives design and engineering teams real-time access to structured local code data, so they can build to spec, not guess.
    </p>
    
    <p>
      Using the <strong>Synthesize Tool</strong>, teams can:
    </p>
    
    <ul>
      <li>Pull relevant requirements like setbacks, FAR, facade treatments, height limits, or berm wall specifications</li>
      <li>Keep that data open side by side while working in tools like <strong><a href="https://www.autodesk.com/products/autocad/overview" target="_blank" rel="noopener noreferrer">AutoCAD</a></strong>, <strong><a href="https://www.autodesk.com/products/revit/architecture" target="_blank" rel="noopener noreferrer">Revit</a></strong>, <strong><a href="https://www.sketchup.com/" target="_blank" rel="noopener noreferrer">SketchUp</a></strong>, or <strong><a href="https://aurorasolar.com/" target="_blank" rel="noopener noreferrer">Aurora Solar</a></strong></li>
      <li>Apply those requirements as the design is being finalized, not after the fact</li>
    </ul>
    
    <p>
      Some teams export Fordje data to build project-specific rule lists or internal pre-inspection checklists that ensure every drawing aligns with local code. Others are working toward integrating Fordje data directly into their design environments.
    </p>
    
    <p>
      The goal is the same: catch issues <em>while you're working</em>, not when you're done.
    </p>
    
    <h2>Who Uses It</h2>
    
    <ul>
      <li>Architects (zoning, facade, FAR, bulk and area, ADA, etc.)</li>
      <li>Clean tech engineering teams (electrical limitations, equipment locations, array layout, planset needs, etc.)</li>
      <li>Housing and commercial design teams (zoning, grading, framing, fire suppression, pipe sizes, GFCI/AFCI, etc.)</li>
    </ul>
    
    <h2>The Outcome</h2>
    
    <p>
      This phase is all about precision. If site assessment is where feasibility gets confirmed, design and engineering is where mistakes get expensive.
    </p>
    
    <p>
      Fordje helps:
    </p>
    
    <ul>
      <li>Prevent rework by having accurate data from the start</li>
      <li>Align designs with exact local requirements</li>
      <li>Give internal and external teams a consistent, up-to-date reference</li>
    </ul>
    
    <p>
      In the next post, we'll cover what happens once you hit submit, <strong>Permitting</strong>, and how Fordje helps teams navigate the fine print without getting stuck.
    </p>
  `,
  coverImage: "/lovable-uploads/2686bf06-f40e-4d17-9b1a-8f952fb5f8d2.png",
  date: "2025-06-24T12:00:00",
  readTime: 4,
  categories: ["Deep Dive", "Design", "Engineering", "Product"],
  author: {
    name: "Gillian Sowray",
    title: "CEO and Co-founder",
    avatar: "/lovable-uploads/69c2731c-f0dc-41d3-8812-8b392d7302c2.png"
  },
  // SEO metadata
  seoKeywords: [
    "design engineering software",
    "construction design tools",
    "building code compliance",
    "architectural design workflow",
    "engineering permit requirements",
    "AutoCAD integration",
    "Revit workflow optimization",
    "construction regulatory data",
    "building design accuracy",
    "permit submission preparation"
  ],
  metaDescription: "Learn how Fordje streamlines design and engineering workflows by providing real-time access to local building codes and requirements. Prevent costly rework and permit delays with accurate regulatory data.",
  socialShareTitle: "How Fordje Prevents Costly Design Rework in Construction Projects",
  socialShareDescription: "Design teams using Fordje catch code compliance issues during the design phase, not after permit submission. See how real-time regulatory data transforms engineering workflows."
};
